/**
 * Wektoryzacja rzutu 2D — wynik kroku wstępnego "wykryj linie" (AI).
 * Współrzędne ZNORMALIZOWANE 0..1 względem wgranego obrazu (niezależne od rozdz.).
 * Używane do walidacji outputu Gemini w floorplan-detect.routes oraz po stronie web
 * do mapowania na płótno edytora.
 */
import { z } from "zod";

const Unit = z.number().min(0).max(1);

export const DetectedWallSchema = z.object({
  x1: Unit,
  y1: Unit,
  x2: Unit,
  y2: Unit,
});

export const DetectedOpeningSchema = z.object({
  type: z.enum(["door", "window"]),
  x: Unit,
  y: Unit,
  /** Szerokość otworu jako ułamek szerokości obrazu. */
  width: Unit.optional(),
  /** Kąt w stopniach (0 = poziomo). */
  angle: z.number().optional(),
});

export const DetectedRoomSchema = z.object({
  label: z.string().max(60),
  x: Unit,
  y: Unit,
});

/** Typy mebli/elementów wnętrza — zgodne z OBJECT_TOOLS w edytorze (FloorPlanEditor). */
export const DetectedFurnitureSchema = z.object({
  type: z.enum([
    "bed", "sofa", "sofa_corner", "armchair", "chair", "bar_stool",
    "table", "cabinet", "kitchen", "kitchen_island", "fridge",
    "shower", "bathtub", "sink", "toilet", "washing_machine",
  ]),
  x: Unit,
  y: Unit,
  /** Szerokość/wysokość elementu jako ułamek wymiarów obrazu (opcjonalne). */
  width: Unit.optional(),
  height: Unit.optional(),
  /** Kąt w stopniach (0 = bez obrotu). */
  angle: z.number().optional(),
});

export const FloorPlanDetectResultSchema = z.object({
  confidence: z.number().min(0).max(1),
  walls: z.array(DetectedWallSchema).default([]),
  openings: z.array(DetectedOpeningSchema).default([]),
  rooms: z.array(DetectedRoomSchema).default([]),
  furniture: z.array(DetectedFurnitureSchema).default([]),
});

export type DetectedWall = z.infer<typeof DetectedWallSchema>;
export type DetectedOpening = z.infer<typeof DetectedOpeningSchema>;
export type DetectedRoom = z.infer<typeof DetectedRoomSchema>;
export type DetectedFurniture = z.infer<typeof DetectedFurnitureSchema>;
export type FloorPlanDetectResult = z.infer<typeof FloorPlanDetectResultSchema>;

/** Pusty wynik (graceful fallback gdy AI nie odczyta rzutu). */
export const EMPTY_DETECT_RESULT: FloorPlanDetectResult = {
  confidence: 0,
  walls: [],
  openings: [],
  rooms: [],
  furniture: [],
};
